// src/utils/gdp.ts
//
// GDP (Ganancia Diaria de Peso, kg/día) por animal, calculado del historial de
// pesajes (JSONB `pesajes`) + opcionalmente `peso_inicial` como baseline.
// Pura, sin imports de DB. Filtro de plausibilidad igual que el bot ([-0.3, 2.5] kg/día).

import { getEventDate } from './dateHelper';

export interface GdpResult {
  /** kg/día (null si no se pudo calcular o cayó fuera del rango plausible). */
  gdp: number | null;
  gainKg: number;
  days: number;
  fromPeso: number;
  toPeso: number;
  fromDate: string;
  toDate: string;
  n: number;
}

const GDP_MIN = -0.3; // kg/día
const GDP_MAX = 2.5;

interface Pesaje {
  peso: number;
  fecha_evento: string;
}

export function computeGdp(
  pesajes?: Pesaje[] | null,
  peso_inicial?: number,
  fecha_de_evento_peso_inicial?: string,
): GdpResult | null {
  const records: Pesaje[] = [];
  if (Array.isArray(pesajes)) {
    for (const p of pesajes) {
      if (p && typeof p.peso === 'number' && p.peso > 0 && p.fecha_evento) records.push({ peso: p.peso, fecha_evento: p.fecha_evento });
    }
  }
  // peso_inicial como baseline si existe y tiene fecha.
  if (peso_inicial && peso_inicial > 0 && fecha_de_evento_peso_inicial) {
    records.push({ peso: peso_inicial, fecha_evento: fecha_de_evento_peso_inicial });
  }
  if (records.length < 2) return null;

  const sorted = records.slice().sort((a, b) => (a.fecha_evento < b.fecha_evento ? -1 : a.fecha_evento > b.fecha_evento ? 1 : 0));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last) return null;

  const gainKg = last.peso - first.peso;
  // getEventDate: date-only como medianoche LOCAL — new Date() crudo lo toma como
  // UTC y mezcla referencias con horas locales (KAN-143).
  const ms = getEventDate(last.fecha_evento).getTime() - getEventDate(first.fecha_evento).getTime();
  const days = ms / 86400000;
  if (!isFinite(days) || days <= 0) return null;

  const gdp = gainKg / days;
  if (gdp < GDP_MIN || gdp > GDP_MAX) return null; // descarta deltas imposibles

  return { gdp, gainKg, days, fromPeso: first.peso, toPeso: last.peso, fromDate: first.fecha_evento, toDate: last.fecha_evento, n: sorted.length };
}

// Benchmark GDP (kg/día) cuando no hay suficientes pesajes para calcular el real.
// Por categoría: TERNERO feedlot (~0,9), NOVILLO pastoreo conservador (~0,5),
// el resto (vaquillona/vaca/toro) más bajo. Default genérico 0,4.
// Fuentes: SciELO (Mendoza), IAPUCO, INTA, IPCVA.
const GDP_BENCHMARKS: Record<string, number> = {
  TERNERO: 0.9,
  NOVILLO: 0.5,
  VAQUILLONA: 0.4,
  VACA: 0.4,
  TORO: 0.3,
};
const GDP_DEFAULT = 0.4;

/** Benchmark GDP (kg/día) según la categoría. Default 0,4 si no aplica. */
export function getBenchmarkGdp(categoria?: string): number {
  if (!categoria) return GDP_DEFAULT;
  return GDP_BENCHMARKS[categoria] ?? GDP_DEFAULT;
}

// Alias histórico (legacy). Mantenido por compatibilidad con quien importe GDP_FALLBACK.
const GDP_BENCHMARK = 0.9;

/** Días estimados para llegar al peso objetivo al ritmo de GDP actual. null si no hay GDP. */
export function daysToTarget(gdp: GdpResult | null, currentPeso: number, target: number): number | null {
  if (!gdp || !gdp.gdp || gdp.gdp <= 0) return null;
  if (currentPeso >= target) return 0;
  return Math.round((target - currentPeso) / gdp.gdp);
}

/**
 * Estima días al objetivo usando el GDP real del animal si hay ≥2 pesajes,
 * o un BENCHMARK promedio (0,9 kg/día) si solo hay 1 pesaje o ninguno.
 *
 * Devuelve { dias, gdpUsado, esEstimacion } para que la UI pueda mostrar
 * "estimado" vs "real" y el GDP usado.
 */
export function estimateDaysToTarget(
  pesoActual: number,
  objetivo: number,
  gdpResult?: GdpResult | null,
): { dias: number | null; gdpUsado: number; esEstimacion: boolean } {
  if (pesoActual >= objetivo) return { dias: 0, gdpUsado: 0, esEstimacion: false };

  // GDP real del animal si hay datos suficientes.
  if (gdpResult?.gdp && gdpResult.gdp > 0) {
    return {
      dias: Math.ceil((objetivo - pesoActual) / gdpResult.gdp),
      gdpUsado: gdpResult.gdp,
      esEstimacion: false,
    };
  }

  // Fallback: benchmark (0,9 kg/día). Útil para animales con un solo pesaje.
  return {
    dias: Math.ceil((objetivo - pesoActual) / GDP_BENCHMARK),
    gdpUsado: GDP_BENCHMARK,
    esEstimacion: true,
  };
}

/**
 * Peso ESTIMADO actual de un animal: último pesaje + GDP × días transcurridos.
 * Usa el GDP real si hay ≥2 pesajes; si no, usa el benchmark por categoría
 * (TERNERO 0,9 / NOVILLO 0,5 / etc.).
 *
 * Esto es lo que hay que comparar contra el objetivo en TODOS los lugares
 * (alertas, listos para vender, barras de progreso) para que coincidan.
 */
export function estimatePesoActual(animal: {
  peso?: number | null;
  pesajes?: Array<{ peso: number; fecha_evento: string }> | null;
  peso_inicial?: number | null;
  fecha_de_evento_peso_inicial?: string | null;
  fecha_ultimo_evento_peso?: string | null;
  fecha_de_evento?: string;
  categoria?: string;
}): number {
  const peso = animal.peso ?? 0;
  if (peso <= 0) return 0;

  // GDP: real si hay datos, sino benchmark por categoría.
  const gdpResult = computeGdp(animal.pesajes, animal.peso_inicial ?? undefined, animal.fecha_de_evento_peso_inicial ?? undefined);
  const benchmark = getBenchmarkGdp(animal.categoria);
  const gdp = gdpResult?.gdp && gdpResult.gdp > 0 ? gdpResult.gdp : benchmark;

  // Fecha del último pesaje.
  const fechaStr = animal.fecha_ultimo_evento_peso ?? animal.fecha_de_evento;
  if (!fechaStr) return peso;

  const dias = Math.max(0, Math.floor((Date.now() - getEventDate(fechaStr).getTime()) / 86400000));
  if (dias === 0) return peso;

  return peso + gdp * dias;
}

/** El benchmark GDP usado como fallback histórico (kg/día). Alias de compatibilidad. */
export const GDP_FALLBACK = GDP_BENCHMARK;
